import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  BookOpen,
  Calendar,
  Building2,
  FileQuestion,
  ChevronRight,
  GraduationCap,
  Languages,
  Calculator,
  Monitor,
  Heart,
  Atom,
  FlaskConical,
  Dna,
  Sigma,
  Landmark,
  Globe,
  Scale,
  Microscope,
  FileText,
  Banknote,
  Briefcase,
  Sprout,
  Home as HomeIcon,
  Flag,
  Eye,
  EyeOff
} from 'lucide-react';
import { boards, years } from '../../data/syllabus';
import { useShuddhoshikhaContext } from '../../hooks/useShuddhoshikhaContext';
import { useFavorites } from '../../hooks/useFavorites';
import { ShuddhoshikhaBreadcrumb, type BreadcrumbSegment } from './ShuddhoshikhaBreadcrumb';
import { normalizeCorrectAnswer } from '../../utils/answerNormalization';
import QuestionSkeleton from './QuestionSkeleton';

interface QuestionBanksProps {
  onBack: () => void;
  isDarkMode?: boolean;
}

type Screen = 'subjects' | 'years' | 'boards' | 'examType' | 'questions';

const iconMap: Record<string, React.ElementType> = {
  BookOpen: BookOpen,
  Languages: Languages,
  Calculator: Calculator,
  Monitor: Monitor,
  Heart: Heart,
  Atom: Atom,
  FlaskConical: FlaskConical,
  Dna: Dna,
  Sigma: Sigma,
  Landmark: Landmark,
  Globe: Globe,
  Scale: Scale,
  Microscope: Microscope,
  FileText: FileText,
  Banknote: Banknote,
  Briefcase: Briefcase,
  Sprout: Sprout,
  Home: HomeIcon,
};

const getSubjectGradient = (subjectId: string): string => {
  const map: Record<string, string> = {
    sub_bn1: 'from-sky-400 to-blue-500 shadow-blue-500/15',
    sub_bn2: 'from-sky-400 to-blue-500 shadow-blue-500/15',
    sub_eng1: 'from-indigo-400 to-purple-500 shadow-purple-500/15',
    sub_eng2: 'from-indigo-400 to-purple-500 shadow-purple-500/15',
    sub_math: 'from-emerald-400 to-teal-500 shadow-teal-500/15',
    sub_ict: 'from-slate-400 to-slate-600 shadow-slate-500/15',
    sub_religion: 'from-rose-400 to-pink-500 shadow-rose-500/15',
    sub_phy: 'from-blue-500 to-indigo-600 shadow-indigo-500/15',
    sub_chem: 'from-cyan-400 to-teal-500 shadow-teal-500/15',
    sub_bio: 'from-green-400 to-emerald-500 shadow-emerald-500/15',
    sub_hmath: 'from-violet-500 to-purple-600 shadow-purple-500/15',
    sub_hist: 'from-amber-500 to-orange-600 shadow-orange-500/15',
    sub_geo: 'from-cyan-400 to-blue-500 shadow-blue-500/15',
    sub_civ: 'from-purple-400 to-indigo-500 shadow-indigo-500/15',
    sub_gsci: 'from-sky-400 to-blue-500 shadow-blue-500/15',
    sub_acc: 'from-teal-400 to-emerald-500 shadow-emerald-500/15',
    sub_fin: 'from-amber-400 to-yellow-500 shadow-amber-500/15',
    sub_bus: 'from-blue-400 to-indigo-500 shadow-indigo-500/15',
    opt_agri: 'from-green-400 to-emerald-500 shadow-emerald-500/15',
    opt_home: 'from-rose-400 to-pink-500 shadow-pink-500/15',
  };
  return map[subjectId] || 'from-sky-400 to-blue-500 shadow-blue-500/15';
};

export default function QuestionBanks({ onBack }: QuestionBanksProps) {
  const shAuth = (window as any).shAuth;
  const [screen, setScreen] = useState<Screen>('subjects');
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [selectedBoard, setSelectedBoard] = useState<string | null>(null);
  const [selectedExamType, setSelectedExamType] = useState<string | null>(null);

  // Question state
  const [questions, setQuestions] = useState<any[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState<boolean>(false);
  const [showAnswers, setShowAnswers] = useState<boolean>(false);
  const [expandedSubQuestions, setExpandedSubQuestions] = useState<Record<string, boolean>>({});
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, number>>({});

  const [flaggedQuestionIds, setFlaggedQuestionIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    const handleFlagged = (e: any) => {
      if (e.detail?.questionId) {
        setFlaggedQuestionIds(prev => {
          const next = new Set(prev);
          next.add(e.detail.questionId);
          return next;
        });
      }
    };
    window.addEventListener('question-flagged' as any, handleFlagged);
    return () => window.removeEventListener('question-flagged' as any, handleFlagged);
  }, []);

   const { subjects, isLoading: contextLoading } = useShuddhoshikhaContext();
  const { isFavorite, toggleFavorite } = useFavorites();

  const getDbSubjectSlug = (subjectId: string, subjectsList: any[]): string => {
    const subjectInfo = subjectsList.find(s => s.id === subjectId);
    if (subjectInfo && (subjectInfo as any).db_slug) {
      return (subjectInfo as any).db_slug;
    }
    const subjectMap: Record<string, string> = {
      'sub_bn1': 'bangla',
      'sub_bn2': 'bangla-2nd',
      'sub_eng1': 'english',
      'sub_eng2': 'english-2nd',
      'sub_math': 'general-math',
      'sub_ict': 'ict',
      'sub_religion': 'religion',
      'sub_phy': 'physics',
      'sub_chem': 'chemistry',
      'sub_bio': 'biology',
      'sub_hmath': 'higher-math',
      'sub_hist': 'history',
      'sub_geo': 'geography',
      'sub_civ': 'civics',
      'sub_gsci': 'general-science',
      'sub_acc': 'accounting',
      'sub_fin': 'finance',
      'sub_bus': 'business',
      'opt_agri': 'agriculture',
      'opt_home': 'home-science'
    };
    return subjectMap[subjectId] || subjectId;
  };

  const loadQuestions = () => {
    if (!selectedSubject || !selectedYear || !selectedBoard || !selectedExamType) return;
    setLoadingQuestions(true);
    setScreen('questions');
    setShowAnswers(false);
    setExpandedSubQuestions({});
    setSelectedAnswers({});
    
    const subjectSlug = getDbSubjectSlug(selectedSubject, subjects);
    const url = `/wp-json/shuddhoshikha/v1/questions?subject_slug=${encodeURIComponent(subjectSlug)}&board_name=${encodeURIComponent(selectedBoard)}&exam_year=${encodeURIComponent(selectedYear)}&question_type=${selectedExamType}&reveal_answers=true`;

    const headers: Record<string, string> = {};
    if (shAuth?.rest_nonce) {
      headers['X-WP-Nonce'] = shAuth.rest_nonce;
    }

    fetch(url, { headers })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((res) => {
        const data = res.data || [];
        setQuestions(data);
      })
      .catch(() => {
        setQuestions([]);
      })
      .finally(() => {
        setLoadingQuestions(false);
      });
  };

  const toggleSubQuestion = (qId: string, label: string) => {
    setExpandedSubQuestions(prev => ({
      ...prev,
      [`${qId}-${label}`]: !prev[`${qId}-${label}`]
    }));
  };

  // Replace initial state with screen subjects
  useEffect(() => {
    const current = window.history.state;
    if (current && current.view === 'চর্চাঘর' && current.subView === 'questionBanks' && !current.screen) {
      window.history.replaceState({
        ...current,
        screen: 'subjects'
      }, '');
    }
  }, []);

  // Sync state changes to browser history
  useEffect(() => {
    const current = window.history.state;
    if (current && current.view === 'চর্চাঘর' && current.subView === 'questionBanks') {
      if (current.screen !== screen) {
        window.history.pushState({
          view: 'চর্চাঘর',
          subView: 'questionBanks',
          screen,
          selectedSubject,
          selectedYear,
          selectedBoard,
          selectedExamType
        }, '');
      }
    }
  }, [screen, selectedSubject, selectedYear, selectedBoard, selectedExamType]);

  // Listen to popstate event (browser back button)
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const state = event.state;
      if (state && state.view === 'চর্চাঘর' && state.subView === 'questionBanks') {
        if (state.screen) setScreen(state.screen as Screen);
        setSelectedSubject(state.selectedSubject !== undefined ? state.selectedSubject : null);
        setSelectedYear(state.selectedYear !== undefined ? state.selectedYear : null);
        setSelectedBoard(state.selectedBoard !== undefined ? state.selectedBoard : null);
        setSelectedExamType(state.selectedExamType !== undefined ? state.selectedExamType : null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const getBreadcrumbSegments = (): BreadcrumbSegment[] => {
    const segments: BreadcrumbSegment[] = [
      { label: 'বোর্ড প্রশ্ন প্রস্তুতি', target: 'subjects' }
    ];
    if (selectedSubject) {
      const subjectName = subjects.find((s) => s.id === selectedSubject)?.name || '';
      segments.push({ label: subjectName, target: 'years' });
    }
    if (selectedYear) {
      segments.push({ label: selectedYear, target: 'boards' });
    }
    if (selectedBoard) {
      segments.push({ label: selectedBoard, target: 'examType' });
    }
    if (selectedExamType) {
      segments.push({ label: selectedExamType === 'mcq' ? 'MCQ' : 'CQ', target: 'examType' });
    }
    return segments;
  };

  const handleNavigate = (target: string) => {
    if (target === 'home') {
      onBack();
    } else if (target === 'subjects') {
      setScreen('subjects');
      setSelectedSubject(null);
      setSelectedYear(null);
      setSelectedBoard(null);
      setSelectedExamType(null);
      setQuestions([]);
    } else if (target === 'years') {
      setScreen('years');
      setSelectedYear(null);
      setSelectedBoard(null);
      setSelectedExamType(null);
      setQuestions([]);
    } else if (target === 'boards') {
      setScreen('boards');
      setSelectedBoard(null);
      setSelectedExamType(null);
      setQuestions([]);
    } else if (target === 'examType') {
      setScreen('examType');
      setSelectedExamType(null);
      setQuestions([]);
    }
  };

  return (
    <div className="space-y-5">
      {/* Dynamic Breadcrumbs */}
      <ShuddhoshikhaBreadcrumb 
        segments={getBreadcrumbSegments()} 
        onNavigate={handleNavigate} 
      />

      {contextLoading ? (
        <div className="space-y-4">
          <div>
            <div className="h-6 w-48 skel-shimmer rounded-lg mb-2" />
            <div className="h-4 w-72 skel-shimmer rounded-lg" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, idx) => (
              <div
                key={idx}
                className="p-5 rounded-2xl bg-white dark:bg-[#1E293B]/60 border border-gray-100 dark:border-white/10 shadow-[0_4px_16px_rgba(0,0,0,0.02)] flex items-center justify-between"
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="w-12 h-12 rounded-2xl skel-shimmer flex-shrink-0" />
                  <div className="flex-1 space-y-2 min-w-0">
                    <div className="h-4 w-2/3 skel-shimmer rounded-md" />
                    <div className="h-3 w-1/3 skel-shimmer rounded-md" />
                  </div>
                </div>
                <div className="w-4 h-4 rounded-full skel-shimmer flex-shrink-0 ml-2" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Screen A: Subject Selection */}
          {screen === 'subjects' && (
            <motion.div
              key="subjects"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              <div>
                <h3 className="font-bangla text-lg font-bold text-[#1F1F1F] dark:text-[#E8EAED]">বোর্ড প্রশ্ন</h3>
                <p className="font-bangla text-sm text-gray-500 dark:text-[#A0A5AD]">বিষয় নির্বাচন করুন</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {subjects.map((subject) => {
                  const Icon = iconMap[subject.icon] || BookOpen;
                  const isSelected = selectedSubject === subject.id;
                  return (
                    <button
                      key={subject.id}
                      onClick={() => {
                        setSelectedSubject(subject.id);
                        setScreen('years');
                      }}
                      className={`group p-5 rounded-2xl bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md border hover:shadow-[0_8px_24px_rgba(0,0,0,0.05)] hover:-translate-y-0.5 transition-all duration-300 text-left flex items-center justify-between cursor-pointer ${
                        isSelected 
                          ? 'border-2 border-[#00CBA9]' 
                          : 'border-gray-100 dark:border-white/5 hover:border-[#00CBA9]/30'
                      }`}
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className={`w-12 h-12 rounded-2xl bg-gradient-to-tr ${getSubjectGradient(subject.id)} flex items-center justify-center text-white shadow-lg border border-white/10 relative overflow-hidden flex-shrink-0`}>
                          <div className="absolute inset-0 bg-gradient-to-b from-white/15 to-transparent pointer-events-none" />
                          <Icon className="w-5.5 h-5.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bangla text-sm font-semibold text-[#1F1F1F] dark:text-[#E8EAED] group-hover:text-[#00CBA9] transition-colors truncate">
                            {subject.name}
                          </h4>
                          <p className="font-english text-xs text-gray-400 mt-1">Board Questions</p>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#00CBA9] transition-colors ml-2 flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Screen B: Year Selection */}
          {screen === 'years' && (
            <motion.div
              key="years"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              <div>
                <h3 className="font-bangla text-lg font-bold text-[#1F1F1F] dark:text-[#E8EAED]">সাল নির্বাচন করুন</h3>
                <p className="font-bangla text-sm text-gray-500 dark:text-[#A0A5AD]">বোর্ড পরীক্ষার সাল বেছে নিন</p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {years.map((year) => (
                  <button
                    key={year}
                    onClick={() => {
                      setSelectedYear(year);
                      setScreen('boards');
                    }}
                    className="group p-5 rounded-2xl bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md border border-gray-100 dark:border-white/5 hover:border-[#00CBA9] hover:bg-[#F4F2FF] dark:hover:bg-[#F4F2FF]/10 transition-all text-center cursor-pointer"
                  >
                    <Calendar className="w-6 h-6 text-[#00CBA9] mx-auto mb-2" />
                    <p className="font-english text-lg font-bold text-[#1F1F1F] dark:text-[#E8EAED] group-hover:text-[#00CBA9] transition-colors">
                      {year}
                    </p>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Screen C: Board Selection */}
          {screen === 'boards' && (
            <motion.div
              key="boards"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              <div>
                <h3 className="font-bangla text-lg font-bold text-[#1F1F1F] dark:text-[#E8EAED]">বোর্ড নির্বাচন করুন</h3>
                <p className="font-bangla text-sm text-gray-500 dark:text-[#A0A5AD]">শিক্ষা বোর্ড বেছে নিন</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {boards.map((board) => (
                  <button
                    key={board}
                    onClick={() => {
                      setSelectedBoard(board);
                      setScreen('examType');
                    }}
                    className="group p-4 rounded-2xl bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md border border-gray-100 dark:border-white/5 hover:border-[#00CBA9] hover:bg-[#F4F2FF] dark:hover:bg-[#F4F2FF]/10 transition-all text-left flex items-center gap-4 cursor-pointer"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#F4F7FB] dark:bg-white/5 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5 text-[#00CBA9]" />
                    </div>
                    <span className="font-bangla text-sm font-medium text-[#1F1F1F] dark:text-[#E8EAED] group-hover:text-[#00CBA9] transition-colors">
                      {board} বোর্ড
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#00CBA9] transition-colors ml-auto flex-shrink-0" />
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Screen D: Exam Type */}
          {screen === 'examType' && (
            <motion.div
              key="examType"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-5"
            >
              <div>
                <h3 className="font-bangla text-lg font-bold text-[#1F1F1F] dark:text-[#E8EAED]">পরীক্ষার ধরণ</h3>
                <p className="font-bangla text-sm text-gray-500 dark:text-[#A0A5AD]">প্রশ্নের ধরণ নির্বাচন করুন</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => setSelectedExamType('mcq')}
                  className={`p-6 rounded-2xl border-2 transition-all text-left cursor-pointer ${
                    selectedExamType === 'mcq'
                      ? 'border-[#00CBA9] bg-[#00CBA9]/5'
                      : 'border-gray-100 dark:border-white/5 bg-white dark:bg-[#1E1E1E]/60 hover:border-[#00CBA9]/30'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                      selectedExamType === 'mcq' ? 'bg-[#00CBA9]/10' : 'bg-[#F4F2FF] dark:bg-white/5'
                    }`}>
                      <FileQuestion className={`w-7 h-7 ${selectedExamType === 'mcq' ? 'text-[#00CBA9]' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <h4 className="font-bangla text-base font-bold text-[#1F1F1F] dark:text-[#E8EAED]">MCQ</h4>
                      <p className="font-bangla text-sm text-gray-500">বহুনির্বাচনি প্রশ্নাবলী</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setSelectedExamType('cq')}
                  className={`p-6 rounded-2xl border-2 transition-all text-left cursor-pointer ${
                    selectedExamType === 'cq'
                      ? 'border-[#00CBA9] bg-[#00CBA9]/5'
                      : 'border-gray-100 dark:border-white/5 bg-white dark:bg-[#1E1E1E]/60 hover:border-[#00CBA9]/30'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                      selectedExamType === 'cq' ? 'bg-[#00CBA9]/10' : 'bg-[#F4F2FF] dark:bg-white/5'
                    }`}>
                      <GraduationCap className={`w-7 h-7 ${selectedExamType === 'cq' ? 'text-[#00CBA9]' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <h4 className="font-bangla text-base font-bold text-[#1F1F1F] dark:text-[#E8EAED]">CQ</h4>
                      <p className="font-bangla text-sm text-gray-500">সৃজনশীল প্রশ্নাবলী</p>
                    </div>
                  </div>
                </button>
              </div>

              {selectedExamType && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-end"
                >
                  <button
                    onClick={loadQuestions}
                    className="flex items-center gap-2 px-8 py-3.5 rounded-2xl bg-[#00CBA9] text-white font-bangla text-sm font-medium hover:bg-[#00b898] transition-colors shadow-lg hover:shadow-xl cursor-pointer"
                  >
                    <FileQuestion className="w-4 h-4" />
                    প্রশ্ন দেখুন
                  </button>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Screen E: Questions Viewer */}
          {screen === 'questions' && (
            <motion.div
              key="questions"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 dark:border-white/5 pb-4">
                <div>
                  <h3 className="font-bangla text-lg font-bold text-[#1F1F1F] dark:text-[#E8EAED]">
                    {selectedExamType === 'mcq' ? 'MCQ অনুশীলন' : 'CQ প্রশ্নপত্র'}
                  </h3>
                  <p className="font-bangla text-sm text-gray-500 dark:text-[#A0A5AD] font-normal">
                    {subjects.find((s) => s.id === selectedSubject)?.name} • {selectedYear} • {selectedBoard} বোর্ড
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowAnswers(!showAnswers)}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[#00CBA9] text-[#00CBA9] hover:bg-[#00CBA9]/5 transition-colors font-bangla text-xs font-semibold cursor-pointer"
                  >
                    {showAnswers ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    {showAnswers ? 'সব উত্তর লুকান' : 'সব উত্তর দেখুন'}
                  </button>

                  <button
                    onClick={() => setScreen('examType')}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-gray-700 dark:text-[#E8EAED] transition-colors font-bangla text-xs font-semibold cursor-pointer"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    ফিরে যান
                  </button>
                </div>
              </div>

              {loadingQuestions ? (
                <QuestionSkeleton qType={selectedExamType as 'mcq' | 'cq' || 'mcq'} />
              ) : questions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center space-y-4 bg-white dark:bg-[#1E1E1E]/40 backdrop-blur-xl border border-gray-100 dark:border-white/5 rounded-3xl">
                  <FileQuestion className="w-16 h-16 text-gray-300 dark:text-white/10" />
                  <div>
                    <h4 className="font-bangla text-base font-bold text-slate-800 dark:text-slate-200">কোনো প্রশ্ন পাওয়া যায়নি।</h4>
                    <p className="font-bangla text-xs text-gray-400 dark:text-gray-500 mt-1 font-normal">এই বিষয়ের অধীনে কোনো প্রশ্ন আপলোড করা হয়নি।</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {questions.map((question, index) => {
                    if (question.type === 'cq') {
                      const parsedCQ = typeof question.question_text === 'string' 
                        ? (() => { try { return JSON.parse(question.question_text); } catch(e) { return {}; } })()
                        : question.question_text;
                      return (
                        <div
                          key={question.id}
                          className="bg-white/60 dark:bg-[#1E1E1E]/40 backdrop-blur-xl dark:backdrop-blur-2xl border border-white/20 dark:border-white/10 rounded-3xl p-6 shadow-[0_8px_32px_rgba(31,38,135,0.04)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.2)] hover:border-[#00CBA9]/20 transition-all duration-300 space-y-5 text-left"
                        >
                          <div className="flex items-start gap-4">
                            <div className="w-8 h-8 rounded-full bg-[#E8F0FE] dark:bg-[#00CBA9]/15 text-[#00CBA9] dark:text-[#8AB4F8] flex items-center justify-center font-english text-xs font-bold flex-shrink-0">
                              {index + 1}
                            </div>
                            <h4 className="font-bangla text-base sm:font-semibold font-normal text-[#1F1F1F] dark:text-[#E8EAED] leading-relaxed pt-0.5">
                              সৃজনশীল প্রশ্ন {index + 1}
                            </h4>
                          </div>

                          {question.stem && (
                            <div className="p-5 rounded-2xl bg-[#F4F7FB] dark:bg-white/5 border-l-4 border-l-[#00CBA9] text-sm leading-relaxed text-[#1F1F1F] dark:text-[#E8EAED] font-bangla">
                              {question.stem}
                            </div>
                          )}

                          <div className="space-y-4 pl-4 md:pl-12">
                            {['ক', 'খ', 'গ', 'ঘ'].map((label) => {
                              const subQ = parsedCQ?.[label] || question[label];
                              if (!subQ) return null;
                              const isExpanded = expandedSubQuestions[`${question.id}-${label}`] || showAnswers;

                              return (
                                <div key={label} className="space-y-2 border-l-2 border-gray-100 dark:border-white/5 pl-4 relative">
                                  <div className="absolute -left-2.5 top-0.5 w-5 h-5 rounded-full bg-[#F4F7FB] dark:bg-white/5 border border-gray-200 dark:border-white/10 flex items-center justify-center font-bangla text-[10px] font-bold text-[#00CBA9]">
                                    {label}
                                  </div>
                                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                                    <p className="font-bangla text-sm text-[#1F1F1F] dark:text-[#E8EAED] leading-relaxed">
                                      {subQ.text || subQ} <span className="text-xs text-gray-400 dark:text-gray-500 font-semibold font-bangla ml-1 font-normal">(মান: {subQ.marks || (label === 'ক' ? 1 : label === 'খ' ? 2 : label === 'গ' ? 3 : 4)})</span>
                                    </p>
                                    <button
                                      onClick={() => toggleSubQuestion(question.id, label)}
                                      className="text-xs text-[#00CBA9] font-bangla font-semibold hover:underline bg-transparent border-none cursor-pointer flex-shrink-0 self-start sm:self-center"
                                    >
                                      {isExpanded ? 'উত্তর লুকান' : 'উত্তর দেখুন'}
                                    </button>
                                  </div>
                                  {isExpanded && (subQ.sub_question_answer || question.hints?.[label] || question.answers?.[label]) && (
                                    <div className="p-4 rounded-2xl bg-amber-500/5 border border-amber-500/10 text-xs text-amber-700 dark:text-amber-300 font-bangla leading-relaxed">
                                      <strong>নমুনা উত্তর:</strong> {subQ.sub_question_answer || question.hints?.[label] || question.answers?.[label]}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          <div className="flex items-center justify-between border-t border-gray-50 dark:border-white/5 pt-4 pl-0 md:pl-12 text-xs text-gray-400 dark:text-[#A0A5AD]">
                            <span className="font-english font-medium">
                              সাল: {question.exam_year || selectedYear}
                            </span>
                          </div>
                        </div>
                      );
                    }

                    const selectedOpt = selectedAnswers[question.id];
                    const hasAnswered = selectedOpt !== undefined;
                    const correctOpt = normalizeCorrectAnswer(question.correct_answer);

                    return (
                      <div
                        key={question.id}
                        className="bg-white/60 dark:bg-[#1E1E1E]/40 backdrop-blur-xl dark:backdrop-blur-2xl border border-white/20 dark:border-white/10 rounded-3xl p-6 shadow-[0_8px_32px_rgba(31,38,135,0.04)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.2)] hover:border-[#00CBA9]/20 transition-all duration-300 space-y-5 text-left"
                      >
                        <div className="flex flex-col-reverse sm:flex-row sm:items-start sm:justify-between gap-4">
                          <div className="flex items-start gap-4 flex-1">
                            {/* Index Box with Green Dot */}
                            <div className="w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-[#8AB4F8] flex items-center justify-center font-english text-xs font-bold relative flex-shrink-0">
                              {index + 1}
                              <div className="w-2 h-2 rounded-full bg-[#00CBA9] absolute -top-0.5 -right-0.5 border border-white dark:border-[#1E1E1E]" />
                            </div>
                            <div className="flex-1 space-y-1.5">
                              <h4 className="font-bangla text-base sm:font-semibold font-normal text-[#1F1F1F] dark:text-[#E8EAED] leading-relaxed pt-0.5 whitespace-pre-line">
                                {question.question_text}
                              </h4>
                            </div>
                          </div>
                          
                          {/* Flag / Fav Icons (no border, text-only icons for max space) */}
                          <div className="flex items-center gap-1 flex-shrink-0 self-end sm:self-auto">
                            <button 
                              onClick={() => (window as any).showReportModal?.(question.id)}
                              className={`p-1.5 rounded-xl transition-all duration-200 cursor-pointer bg-transparent border-none ${flaggedQuestionIds.has(question.id) ? 'text-[#00CBA9] bg-emerald-50 dark:bg-emerald-950/20' : 'text-slate-400 hover:text-[#00CBA9] hover:bg-teal-50 dark:hover:bg-teal-950/20'}`}
                              title="রিপোর্ট করুন"
                            >
                              <Flag className={`w-4 h-4 ${flaggedQuestionIds.has(question.id) ? 'text-[#00CBA9] fill-[#00CBA9]' : ''}`} />
                            </button>
                            <button 
                               onClick={() => toggleFavorite({
                                 id: question.id,
                                 question_text: question.question_text,
                                 options: question.options,
                                 correct_answer: question.correct_answer,
                                 explanation: question.explanation,
                                 stem: question.stem,
                                 reference: question.reference,
                                 subject_id: selectedSubject || undefined,
                                 subject_name: selectedSubject ? (subjects.find(s => s.id === selectedSubject)?.name || undefined) : undefined
                               })}
                               className="p-1.5 transition-colors cursor-pointer bg-transparent border-none"
                             >
                               <Heart className={`w-4 h-4 ${isFavorite(question.id) ? 'text-rose-500 fill-rose-500' : 'text-gray-400 dark:text-gray-500 hover:text-rose-500'}`} />
                             </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-3 pl-0 md:pl-12">
                          {question.options && question.options.map((option: string, optionIndex: number) => {
                            const isSelected = selectedOpt === optionIndex;
                            const isCorrect = optionIndex === correctOpt;

                            let optionStyle = 'border-gray-200 dark:border-white/10 bg-white/50 dark:bg-white/5 hover:border-[#00CBA9]/30 dark:hover:border-white/15 hover:bg-white dark:hover:bg-white/10 hover:shadow-sm';
                            let badgeStyle = 'bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400';

                            if (showAnswers || hasAnswered) {
                              if (isCorrect) {
                                optionStyle = 'border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400';
                                badgeStyle = 'bg-emerald-500 text-white';
                              } else if (isSelected) {
                                optionStyle = 'border-rose-500 bg-rose-500/10 text-rose-700 dark:text-rose-400';
                                badgeStyle = 'bg-rose-500 text-white';
                              }
                            } else if (isSelected) {
                              optionStyle = 'border-[#00CBA9] bg-[#00CBA9]/5 text-[#00CBA9] dark:text-[#8AB4F8]';
                              badgeStyle = 'bg-[#00CBA9] text-white';
                            }

                            return (
                              <button
                                key={optionIndex}
                                onClick={() => {
                                  if (!showAnswers && !hasAnswered) {
                                    setSelectedAnswers({ ...selectedAnswers, [question.id]: optionIndex });

                                    // Asynchronous non-blocking sync to the database
                                    const optionsLabels = ['ক', 'খ', 'গ', 'ঘ'];
                                    const isCorrect = optionIndex === correctOpt;
                                    
                                    const mapFrontendToDb = (id: string): string => {
                                      const map: Record<string, string> = {
                                        'sub_bn1': 'bangla', 'sub_bn2': 'bangla-2nd', 'sub_eng1': 'english', 'sub_eng2': 'english-2nd',
                                        'sub_math': 'general-math', 'sub_ict': 'ict', 'sub_religion': 'religion', 'sub_phy': 'physics',
                                        'sub_chem': 'chemistry', 'sub_bio': 'biology', 'sub_hmath': 'higher-math', 'sub_hist': 'history',
                                        'sub_geo': 'geography', 'sub_civ': 'civics', 'sub_gsci': 'general-science', 'sub_acc': 'accounting',
                                        'sub_fin': 'finance', 'sub_bus': 'business', 'sub_eco': 'economics', 'opt_agri': 'agriculture',
                                        'opt_home': 'home-science'
                                      };
                                      return map[id] || id;
                                    };
                                    const dbSubject = selectedSubject ? mapFrontendToDb(selectedSubject) : 'general';

                                    const headers: Record<string, string> = {
                                      'Content-Type': 'application/json'
                                    };
                                    if (shAuth?.rest_nonce) {
                                      headers['X-WP-Nonce'] = shAuth.rest_nonce;
                                    }

                                    fetch('/wp-json/shuddhoshikha/v1/sync-history', {
                                      method: 'POST',
                                      headers: headers,
                                      body: JSON.stringify({
                                        action: 'sync_practice_answer',
                                        question_id: question.id,
                                        chosen_option: optionsLabels[optionIndex],
                                        correct_option: correctOpt !== undefined && correctOpt !== null ? optionsLabels[correctOpt] : null,
                                        is_correct: isCorrect,
                                        subject_category: dbSubject
                                      }),
                                      keepalive: true
                                    }).catch(err => console.error('Failed to sync practice answer:', err));
                                  }
                                }}
                                className={`group flex items-center p-3.5 rounded-2xl border transition-all text-left w-full cursor-pointer ${optionStyle}`}
                              >
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bangla text-xs font-bold mr-3.5 flex-shrink-0 transition-colors ${badgeStyle}`}>
                                  {['ক', 'খ', 'গ', 'ঘ'][optionIndex]}
                                </div>
                                <span className="font-bangla text-sm sm:font-medium font-normal leading-relaxed">
                                  {option}
                                </span>
                              </button>
                            );
                          })}
                        </div>

                        <div className="flex items-center justify-between border-t border-gray-50 dark:border-white/5 pt-4 pl-0 md:pl-12 text-xs text-gray-400 dark:text-[#A0A5AD]">
                          <span className="font-english font-medium">
                            সাল: {question.exam_year || selectedYear}
                          </span>
                        </div>

                        {(showAnswers || hasAnswered) && question.explanation && (
                          <div className="mt-3 p-4 rounded-2xl bg-amber-500/5 border border-amber-500/10 text-xs text-amber-700 dark:text-amber-300 pl-4 md:pl-12 leading-relaxed">
                            <strong>ব্যাখ্যা:</strong> {question.explanation}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}
          {screen !== 'questions' && (
            <div className="flex justify-start pt-6 md:hidden">
              <button
                onClick={() => {
                  if (screen === 'subjects') {
                    onBack();
                  } else if (screen === 'years') {
                    handleNavigate('subjects');
                  } else if (screen === 'boards') {
                    handleNavigate('years');
                  } else if (screen === 'examType') {
                    handleNavigate('boards');
                  } else {
                    onBack();
                  }
                }}
                className="flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-white dark:bg-[#1E1E1E]/60 border border-gray-200 dark:border-white/5 text-gray-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5 hover:border-gray-300 dark:hover:border-white/10 active:scale-95 transition-all cursor-pointer font-bangla text-sm font-bold shadow-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>পেছনে যান</span>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
